import { useEffect, useState } from "react"
import { SearchOutlined } from "@ant-design/icons"
import {
  Button,
  Flex,
  Input,
  Modal,
  Select,
  Table,
  Tag,
  Typography,
  message,
} from "antd"
import type { TableProps } from "antd"
import { changeStaffUser, listStaffUsers } from "~/api/staff"
import { MOBILE_PHONE_PATTERN, UserLevelType } from "~/common/constants"
import {
  formatAmount,
  formatDateTimeMinute,
  isAbortError,
} from "~/common/utils"
import styles from "./index.css"

const { Text, Title } = Typography
const pageSize = 10
const mainSiteUrl = "https://www.kadabao.com/"
const levelOptions = [
  { label: "免费版", value: UserLevelType.free },
  { label: "专业版", value: UserLevelType.pro },
]

const confirmChangeUser = (user: API.Staff.StaffUser) => {
  const userId = user.id

  if (!userId) {
    void message.warning("缺少用户ID，无法切换")
    return
  }

  Modal.confirm({
    title: "确认切换用户",
    content: (
      <Flex
        vertical
        gap={8}
      >
        <Text>确认切换为以下用户？</Text>
        <Text>手机号：{user.mobile}</Text>
        <Text type="secondary">用户ID：{userId}</Text>
      </Flex>
    ),
    okText: "确认切换",
    cancelText: "取消",
    onOk: async () => {
      const response = await changeStaffUser({
        user_id: userId,
        redirect: mainSiteUrl,
      })

      window.location.assign(response.data?.redirect || mainSiteUrl)
    },
  })
}

export const StaffUserList = () => {
  const [users, setUsers] = useState<API.Staff.StaffUserListItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [userIdInput, setUserIdInput] = useState("")
  const [userIdFilter, setUserIdFilter] = useState("")
  const [mobileInput, setMobileInput] = useState("")
  const [mobileFilter, setMobileFilter] = useState("")
  const [levelCodeInput, setLevelCodeInput] = useState<
    UserLevelType | undefined
  >()
  const [levelCodeFilter, setLevelCodeFilter] = useState<
    UserLevelType | undefined
  >()
  const [searchVersion, setSearchVersion] = useState(0)

  const searchUsers = () => {
    const userId = userIdInput.trim()
    const mobile = mobileInput.trim()

    if (mobile && !MOBILE_PHONE_PATTERN.test(mobile)) {
      void message.warning("请输入正确的手机号")
      return
    }

    setPage(1)
    setUserIdFilter(userId)
    setMobileFilter(mobile)
    setLevelCodeFilter(levelCodeInput)
    setSearchVersion((version) => version + 1)
  }

  useEffect(() => {
    const controller = new AbortController()

    const loadUsers = async () => {
      setLoading(true)

      try {
        const response = await listStaffUsers(
          {
            ...(userIdFilter ? { user_id: userIdFilter } : {}),
            ...(mobileFilter ? { mobile: mobileFilter } : {}),
            ...(levelCodeFilter !== undefined
              ? { level_code: levelCodeFilter }
              : {}),
            page: page - 1,
            page_size: pageSize,
          },
          controller.signal,
        )

        if (!controller.signal.aborted) {
          setUsers(response.data?.items || [])
          setTotal(response.data?.total || 0)
        }
      } catch (error) {
        if (!isAbortError(error)) {
          throw error
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }

    void loadUsers()
    return () => controller.abort()
  }, [levelCodeFilter, mobileFilter, page, searchVersion, userIdFilter])

  const columns: TableProps<API.Staff.StaffUserListItem>["columns"] = [
    {
      title: "用户手机号",
      dataIndex: "mobile",
      width: 160,
    },
    {
      title: "用户ID",
      dataIndex: "id",
      ellipsis: true,
    },
    {
      title: "用户等级",
      width: 120,
      render: (_, record) => {
        const isPro = record.level_info?.level_code === UserLevelType.pro

        return (
          <Tag color={isPro ? "blue" : "default"}>
            {isPro ? "专业版" : "免费版"}
          </Tag>
        )
      },
    },
    {
      title: "到期时间",
      width: 180,
      render: (_, record) => {
        if (record.level_info?.level_code !== UserLevelType.pro) return "-"

        return formatDateTimeMinute(record.level_info.level_effective_until)
      },
    },
    {
      title: "订购专业版次数",
      dataIndex: "pro_order_count",
      width: 160,
      align: "right",
      render: (value?: number) => value ?? 0,
    },
    {
      title: "充值积点金额",
      dataIndex: "wallet_paid_amount",
      width: 160,
      align: "right",
      render: (value?: number) => formatAmount(value ?? 0),
    },
    {
      title: "累计付费金额",
      dataIndex: "total_paid_amount",
      width: 160,
      align: "right",
      render: (value?: number) => formatAmount(value ?? 0),
    },
    {
      title: "操作",
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => confirmChangeUser(record)}
        >
          切换用户
        </Button>
      ),
    },
  ]

  return (
    <Flex
      className={styles.page}
      vertical
      gap={18}
    >
      <Flex
        vertical
        gap={6}
      >
        <Title level={2}>用户列表</Title>
        <Text type="secondary">
          查看平台注册用户、当前会员状态及累计付费情况。付费统计不含赠送，退款不扣减。
        </Text>
      </Flex>

      <section className={styles.searchPanel}>
        <Flex
          gap={12}
          wrap="wrap"
        >
          <Input
            className={styles.searchInput}
            allowClear
            placeholder="请输入用户ID"
            value={userIdInput}
            onChange={(event) => setUserIdInput(event.target.value)}
            onPressEnter={searchUsers}
          />
          <Input
            className={styles.searchInput}
            allowClear
            maxLength={11}
            placeholder="请输入用户手机号"
            value={mobileInput}
            onChange={(event) => setMobileInput(event.target.value)}
            onPressEnter={searchUsers}
          />
          <Select<UserLevelType>
            className={styles.levelSelect}
            allowClear
            options={levelOptions}
            placeholder="请选择用户版本"
            value={levelCodeInput}
            onChange={setLevelCodeInput}
          />
          <Button
            icon={<SearchOutlined />}
            loading={loading}
            type="primary"
            onClick={searchUsers}
          >
            检索
          </Button>
        </Flex>
      </section>

      <section className={styles.listPanel}>
        <Table
          columns={columns}
          dataSource={users}
          loading={loading}
          rowKey={(record) => record.id || record.mobile}
          pagination={{
            current: page,
            pageSize,
            showSizeChanger: false,
            total,
            onChange: setPage,
          }}
          scroll={{ x: 1280 }}
        />
      </section>
    </Flex>
  )
}
